/**
 * Syncs the product media gallery with variant metafield images
 * (custom.productimages) when the selected color/variant changes.
 * Also preloads other variant image sets so switching feels instant.
 */
(function () {
  const jsonCache = new Map();
  const preloadedSections = new Set();
  const DEFAULT_SIZES =
    '(min-width: 1200px) calc((1200px - 10rem) / 2), (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
  const MAX_CONCURRENT = 3;

  function parseJsonScript(id) {
    if (jsonCache.has(id)) return jsonCache.get(id);

    const el = document.getElementById(id);
    if (!el) {
      jsonCache.set(id, {});
      return {};
    }

    try {
      const data = JSON.parse(el.textContent);
      jsonCache.set(id, data);
      return data;
    } catch (error) {
      console.error('Failed to parse', id, error);
      jsonCache.set(id, {});
      return {};
    }
  }

  function getImagesForVariant(sectionId, variantId) {
    if (!variantId) return null;

    const mediaMap = parseJsonScript(`VariantMediaMap-${sectionId}`);
    const colorMap = parseJsonScript(`VariantColorMap-${sectionId}`);
    const variantKey = String(variantId);

    if (mediaMap[variantKey]?.length) {
      return mediaMap[variantKey];
    }

    const color = colorMap[variantKey];
    if (!color) return null;

    for (const [id, images] of Object.entries(mediaMap)) {
      if (colorMap[id] === color && images?.length) {
        return images;
      }
    }

    return null;
  }

  function syncGallery(sectionId, variantId) {
    const images = getImagesForVariant(sectionId, variantId);
    if (!images?.length) return;

    const gallery = document.querySelector(`#MediaGallery-${sectionId}`);
    gallery?.replaceWithVariantImages?.(images, sectionId);
  }

  function getCurrentVariantId(sectionId) {
    const productInfo = document.querySelector(`product-info[data-section="${sectionId}"]`);
    return (
      productInfo?.querySelector(`#product-form-${sectionId} input[name="id"]`)?.value ||
      productInfo?.querySelector('input[name="id"]')?.value ||
      null
    );
  }

  function getGallerySizes(sectionId) {
    const gallery = document.querySelector(`#MediaGallery-${sectionId}`);
    return gallery?.dataset?.imageSizes || DEFAULT_SIZES;
  }

  function imageSignature(images) {
    if (!images?.length) return '';
    return images.map((image) => image.src).join('|');
  }

  /**
   * Build a deduped preload queue:
   * 1) first image of each unique gallery set (most important for switch UX)
   * 2) remaining main images
   * 3) thumbnails
   * Skips the currently active variant set (already on screen / loading).
   */
  function buildPreloadQueue(sectionId) {
    const mediaMap = parseJsonScript(`VariantMediaMap-${sectionId}`);
    const currentVariantId = getCurrentVariantId(sectionId);
    const currentSignature = imageSignature(getImagesForVariant(sectionId, currentVariantId));
    const sizes = getGallerySizes(sectionId);

    const seenSets = new Set();
    const seenUrls = new Set();
    const firstPass = [];
    const restPass = [];
    const thumbPass = [];

    for (const images of Object.values(mediaMap)) {
      if (!images?.length) continue;

      const signature = imageSignature(images);
      if (!signature || seenSets.has(signature)) continue;
      seenSets.add(signature);

      const isCurrent = signature === currentSignature;

      images.forEach((image, index) => {
        if (image.src && !seenUrls.has(image.src)) {
          seenUrls.add(image.src);
          const item = { src: image.src, srcset: image.srcset, sizes };
          if (isCurrent) {
            // Current set may still have lazy slides — warm them after first paint.
            if (index > 0) restPass.push(item);
          } else if (index === 0) {
            firstPass.push(item);
          } else {
            restPass.push(item);
          }
        }

        if (image.thumb && !seenUrls.has(image.thumb)) {
          seenUrls.add(image.thumb);
          if (!(isCurrent && index === 0)) {
            thumbPass.push({ src: image.thumb });
          }
        }
      });
    }

    return [...firstPass, ...restPass, ...thumbPass];
  }

  function preloadOne({ src, srcset, sizes }) {
    return new Promise((resolve) => {
      const img = new Image();
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      img.onload = done;
      img.onerror = done;

      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      img.src = src;

      // Already cached / decoded
      if (img.complete) done();
    });
  }

  function runQueue(queue, concurrency) {
    let index = 0;
    let active = 0;

    return new Promise((resolve) => {
      const next = () => {
        if (index >= queue.length && active === 0) {
          resolve();
          return;
        }

        while (active < concurrency && index < queue.length) {
          const item = queue[index++];
          active += 1;
          preloadOne(item).finally(() => {
            active -= 1;
            next();
          });
        }
      };

      next();
    });
  }

  function whenBrowserIdle(callback) {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 200);
    }
  }

  function schedulePreload(sectionId) {
    if (preloadedSections.has(sectionId)) return;
    if (!document.getElementById(`VariantMediaMap-${sectionId}`)) return;

    preloadedSections.add(sectionId);

    const start = () => {
      whenBrowserIdle(() => {
        const queue = buildPreloadQueue(sectionId);
        if (!queue.length) return;
        runQueue(queue, MAX_CONCURRENT);
      });
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  function initSection(sectionId) {
    if (!document.getElementById(`VariantMediaMap-${sectionId}`)) return;

    const variantId = getCurrentVariantId(sectionId);
    if (variantId) syncGallery(sectionId, variantId);

    schedulePreload(sectionId);
  }

  function init() {
    document.querySelectorAll('product-info[data-section]').forEach((productInfo) => {
      initSection(productInfo.dataset.section);
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data?.variant?.id || !data?.sectionId) return;
        syncGallery(data.sectionId, data.variant.id);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      customElements.whenDefined('media-gallery').then(init);
    });
  } else {
    customElements.whenDefined('media-gallery').then(init);
  }
})();
